import { homeAPI } from '../../home/api/homeAPI';
import { menuListAdapter } from '../../home/libs/menuAdapter';
import { IProductInfo } from '../../home/types/Menu';
import {
  BasketItem,
  matchBasketItemsWithMenu,
} from '../../../store/slices/basketSlice';
import { saveStoredBasket } from '../../basket/libs/basketStorage';

/**
 * Marks the basket against a branch's menu and persists the result.
 *
 * Persisting matters: the cart re-reads storage when it opens, so a
 * redux-only flag would be wiped on arrival and missing items would look
 * available again. Writing through also bypasses the reducer's "all
 * unavailable looks bogus" guard — right to be sceptical of a background
 * menu diff, wrong here where the branch menu was fetched deliberately.
 *
 * Returns the marked items; the caller dispatches them.
 */
export const applyBranchAvailability = (
  items: BasketItem[],
  branchMenu: IProductInfo[]
): BasketItem[] => {
  const marked = matchBasketItemsWithMenu(items, branchMenu);
  saveStoredBasket(marked);

  return marked;
};

export type BranchAvailability = {
  unavailable: BasketItem[];
  branchMenu: IProductInfo[];
};

// There's no backend endpoint to check per-branch stock for pickup, so this
// mirrors the same on-device check already used when a delivery order's
// nearest branch changes (see addresses/services/getNearBranch.ts): fetch the
// branch's menu and diff it against what's in the basket.
//
// Deliberately read-only — it neither dispatches nor writes storage, so a
// user who backs out of the confirmation keeps their basket exactly as it was.
// The caller applies the result only once the removal is confirmed.
export const checkBranchAvailability = async (
  branchId: number,
  basketItems: BasketItem[]
): Promise<BranchAvailability> => {
  const empty: BranchAvailability = { unavailable: [], branchMenu: [] };

  if (!basketItems.length) return empty;

  const res = await homeAPI.getMenus(branchId);
  const branchMenu = menuListAdapter(res?.data).flatMap(
    category => category.menu
  );

  // An empty menu means the request gave us nothing usable — not that the
  // branch stocks nothing. Treat it as "couldn't verify" and leave the basket
  // untouched, rather than declaring every item out of stock.
  if (!branchMenu.length) return empty;

  return {
    unavailable: matchBasketItemsWithMenu(basketItems, branchMenu).filter(
      item => !item.isAvailable
    ),
    branchMenu,
  };
};
